import { useState } from "react";
import { motion } from "framer-motion";
import { ShoppingBag, Heart } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useCartStore } from "@/stores/cartStore";
import { ShopifyProduct } from "@/lib/shopify";
import { useCurrency } from "@/contexts/CurrencyContext";
import { useWishlist } from "@/contexts/WishlistContext";
import { toast } from "sonner";
import VariantPickerDialog from "@/components/VariantPickerDialog";

interface Props {
  product: ShopifyProduct;
  index: number;
}

const FORCE_SOLD_OUT_HANDLES = [
  "the-neo-blush-pro-essentials-set",
];

const ShopifyProductCard = ({ product, index }: Props) => {
  const addItem = useCartStore(state => state.addItem);
  const isLoading = useCartStore(state => state.isLoading);
  const { formatPrice } = useCurrency();
  const { isInWishlist, toggleWishlist } = useWishlist();
  const { node } = product;
  const image = node.images.edges[0]?.node;
  const variants = node.variants.edges;
  const variant = variants[0]?.node;
  const hasMultipleVariants = variants.length > 1;
  const price = parseFloat(node.priceRange.minVariantPrice.amount);
  const isFavorited = isInWishlist(node.id);
  const isAvailable = !FORCE_SOLD_OUT_HANDLES.includes(node.handle) && !!variant?.availableForSale;
  const [pickerOpen, setPickerOpen] = useState(false);

  const handleAddToCart = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!variant || !isAvailable) return;
    if (hasMultipleVariants) {
      setPickerOpen(true);
      return;
    }
    await addItem({
      product,
      variantId: variant.id,
      variantTitle: variant.title,
      price: variant.price,
      quantity: 1,
      selectedOptions: variant.selectedOptions || [],
    });
    toast.success(`${node.title} added to cart`);
  };

  const handleToggleWishlist = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    toggleWishlist(product);
    toast.success(isFavorited ? `${node.title} removed from wishlist` : `${node.title} added to wishlist`);
  };

  return (
    <>
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05, duration: 0.5 }}
      viewport={{ once: true }}
      className="group relative"
    >
      <Link to={`/product/${node.handle}`}>
        <div className="relative overflow-hidden rounded-2xl bg-card border border-border/50 transition-all duration-500 group-hover:border-primary/30 group-hover:shadow-[0_0_40px_hsl(350,100%,88%,0.15)]">
          {/* Product Image */}
          <div className="relative aspect-square p-4 flex items-center justify-center bg-gradient-to-br from-secondary/50 to-background overflow-hidden">
            {image ? (
              <img
                src={image.url}
                alt={image.altText || node.title}
                className="w-full h-full object-cover rounded-lg transition-transform duration-500 group-hover:scale-105"
                loading="lazy"
              />
            ) : (
              <div className="w-full h-full bg-muted rounded-lg flex items-center justify-center">
                <span className="text-muted-foreground text-sm font-subtitle">No Image</span>
              </div>
            )}

            {/* Favorite Button */}
            <button
              onClick={handleToggleWishlist}
              aria-label={isFavorited ? `Remove ${node.title} from wishlist` : `Add ${node.title} to wishlist`}
              className="absolute top-2 right-2 z-10 h-10 w-10 flex items-center justify-center rounded-full bg-background/80 backdrop-blur-sm border border-border/50 hover:bg-background transition-all duration-200 hover:scale-110"
            >
              <Heart
                className={`w-4 h-4 transition-colors ${isFavorited ? 'text-primary fill-primary' : 'text-muted-foreground hover:text-primary'}`}
              />
            </button>

            {/* Quick Add Overlay (desktop hover) */}
            <div className="hidden sm:flex absolute inset-0 bg-background/80 backdrop-blur-sm items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              <Button
                variant="glow"
                size="lg"
                className="gap-2"
                onClick={handleAddToCart}
                disabled={isLoading || !isAvailable}
              >
                <ShoppingBag className="w-4 h-4" />
                {isAvailable
                  ? hasMultipleVariants ? "Choose Shade" : "Add to Cart"
                  : "Sold Out"}
              </Button>
            </div>
          </div>

          {/* Product Info */}
          <div className="p-4 sm:p-5">
            {node.productType && (
              <p className="text-xs font-subtitle text-primary uppercase tracking-wider mb-1">
                {node.productType}
              </p>
            )}
            <h3 className="font-display text-lg font-medium text-foreground mb-2 group-hover:text-primary transition-colors line-clamp-2">
              {node.title}
            </h3>
            <p className="font-subtitle text-xl font-semibold text-foreground">
              {formatPrice(price)}
            </p>
            {hasMultipleVariants && (
              <p className="text-xs font-subtitle text-muted-foreground mt-1">
                {variants.length} variants
              </p>
            )}
            {/* Mobile Add Button (always visible on touch) */}
            <Button
              variant="glowOutline"
              size="sm"
              className="sm:hidden w-full mt-3 gap-2 h-11"
              onClick={handleAddToCart}
              disabled={isLoading || !isAvailable}
            >
              <ShoppingBag className="w-4 h-4" />
              {isAvailable
                ? hasMultipleVariants ? "Choose Shade" : "Add to Cart"
                : "Sold Out"}
            </Button>
          </div>
        </div>
      </Link>
    </motion.div>
    <VariantPickerDialog product={product} open={pickerOpen} onOpenChange={setPickerOpen} />
    </>
  );
};

export default ShopifyProductCard;
